import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import NavBar from "@/components/NavBar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/hooks/use-toast";
import {
  Settings,
  Shield,
  Users,
  Database,
  Bell,
  Save,
  ArrowLeft,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Key,
  Server,
  Activity,
  BarChart3,
  Globe,
  Lock,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

// Form schemas
const platformSettingsSchema = z.object({
  siteName: z.string().min(1, "Site name is required"),
  siteDescription: z.string().optional(),
  maintenanceMode: z.boolean().default(false),
  allowRegistrations: z.boolean().default(true),
  requireEmailVerification: z.boolean().default(true),
  maxCampaignsPerUser: z.number().min(1).max(100),
  maxResponsesPerCampaign: z.number().min(10).max(10000),
  autoArchiveInactiveDays: z.number().min(30).max(365),
  enableAnalytics: z.boolean().default(true),
  enableNotifications: z.boolean().default(true),
  enableEmailSharing: z.boolean().default(true),
  defaultEmailShareLimit: z.number().min(1).max(1000),
});

const securitySettingsSchema = z.object({
  sessionTimeout: z.number().min(15).max(1440), // minutes
  maxLoginAttempts: z.number().min(3).max(10),
  lockoutDuration: z.number().min(5).max(60), // minutes
  requireTwoFactor: z.boolean().default(false),
  enableAuditLog: z.boolean().default(true),
  dataRetentionDays: z.number().min(30).max(2555), // 7 years max
  enableBackup: z.boolean().default(true),
  backupFrequency: z.enum(["daily", "weekly", "monthly"]),
});

type PlatformSettingsValues = z.infer<typeof platformSettingsSchema>;
type SecuritySettingsValues = z.infer<typeof securitySettingsSchema>;

const AdminSettings: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("platform");
  const [isLoading, setIsLoading] = useState(false);

  // Platform settings form
  const platformForm = useForm<PlatformSettingsValues>({
    resolver: zodResolver(platformSettingsSchema),
    defaultValues: {
      siteName: "FundOrFling",
      siteDescription: "Product validation platform for entrepreneurs",
      maintenanceMode: false,
      allowRegistrations: true,
      requireEmailVerification: true,
      maxCampaignsPerUser: 10,
      maxResponsesPerCampaign: 1000,
      autoArchiveInactiveDays: 90,
      enableAnalytics: true,
      enableNotifications: true,
      enableEmailSharing: true,
      defaultEmailShareLimit: 100,
    },
  });

  // Security settings form
  const securityForm = useForm<SecuritySettingsValues>({
    resolver: zodResolver(securitySettingsSchema),
    defaultValues: {
      sessionTimeout: 120, // 2 hours
      maxLoginAttempts: 5,
      lockoutDuration: 15, // 15 minutes
      requireTwoFactor: false,
      enableAuditLog: true,
      dataRetentionDays: 1095, // 3 years
      enableBackup: true,
      backupFrequency: "daily",
    },
  });

  // Check if user is admin
  if (!isAuthenticated || user?.role !== "ADMIN") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-24 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-slate-900 mb-4">
                Access Denied
              </h1>
              <p className="text-slate-600">
                You need admin privileges to access these settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const onPlatformSubmit = async (data: PlatformSettingsValues) => {
    setIsLoading(true);
    try {
      // TODO: Implement API call to save platform settings
      console.log("Platform settings:", data);
      toast({
        title: "Settings saved successfully",
        description: "Platform settings have been updated.",
      });
    } catch (error) {
      toast({
        title: "Error saving settings",
        description: "Failed to save platform settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSecuritySubmit = async (data: SecuritySettingsValues) => {
    setIsLoading(true);
    try {
      // TODO: Implement API call to save security settings
      console.log("Security settings:", data);
      toast({
        title: "Settings saved successfully",
        description: "Security settings have been updated.",
      });
    } catch (error) {
      toast({
        title: "Error saving settings",
        description: "Failed to save security settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      <NavBar />
      <div className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              variant="ghost"
              onClick={() => navigate("/admin")}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-r from-primary to-accent">
                <Settings className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Admin Settings
                </h1>
                <p className="text-muted-foreground mt-1">
                  Configure platform settings and security policies
                </p>
              </div>
            </div>
          </motion.div>

          {/* Settings Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-muted/50 to-transparent border-b border-border/50">
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="w-full"
                >
                  <TabsList className="w-full justify-start mb-6 bg-card/50 backdrop-blur-sm border border-border/50 h-12">
                    <TabsTrigger
                      value="platform"
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-accent data-[state=active]:text-white flex items-center gap-2"
                    >
                      <Globe size={16} />
                      Platform Settings
                    </TabsTrigger>
                    <TabsTrigger
                      value="security"
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-accent data-[state=active]:to-primary data-[state=active]:text-white flex items-center gap-2"
                    >
                      <Shield size={16} />
                      Security & Privacy
                    </TabsTrigger>
                  </TabsList>

                  {/* Platform Settings Tab */}
                  <TabsContent value="platform" className="mt-0 space-y-6 p-6">
                    <Form {...platformForm}>
                      <form
                        onSubmit={platformForm.handleSubmit(onPlatformSubmit)}
                        className="space-y-8"
                      >
                        {/* General Settings */}
                        <div className="space-y-6">
                          <div>
                            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                              <Globe className="w-5 h-5 text-primary" />
                              General Settings
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <FormField
                                control={platformForm.control}
                                name="siteName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Site Name</FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="FundORFling"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormDescription>
                                      The name displayed throughout the platform
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={platformForm.control}
                                name="siteDescription"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Site Description</FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="Product validation platform"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormDescription>
                                      Brief description of the platform
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>

                          <Separator />

                          {/* User Management */}
                          <div>
                            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                              <Users className="w-5 h-5 text-primary" />
                              User Management
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <FormField
                                control={platformForm.control}
                                name="allowRegistrations"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Allow New Registrations
                                      </FormLabel>
                                      <FormDescription>
                                        Enable or disable new user registrations
                                      </FormDescription>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={platformForm.control}
                                name="requireEmailVerification"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Email Verification
                                      </FormLabel>
                                      <FormDescription>
                                        Require email verification for new
                                        accounts
                                      </FormDescription>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={platformForm.control}
                                name="maxCampaignsPerUser"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      Max Campaigns Per User
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="1"
                                        max="100"
                                        {...field}
                                        onChange={(e) =>
                                          field.onChange(
                                            parseInt(e.target.value)
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormDescription>
                                      Maximum number of campaigns a user can
                                      create
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={platformForm.control}
                                name="maxResponsesPerCampaign"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      Max Responses Per Campaign
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="10"
                                        max="10000"
                                        {...field}
                                        onChange={(e) =>
                                          field.onChange(
                                            parseInt(e.target.value)
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormDescription>
                                      Maximum number of responses a campaign can
                                      receive
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>

                          <Separator />

                          {/* System Settings */}
                          <div>
                            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                              <Server className="w-5 h-5 text-primary" />
                              System Settings
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <FormField
                                control={platformForm.control}
                                name="maintenanceMode"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Maintenance Mode
                                      </FormLabel>
                                      <FormDescription>
                                        Enable maintenance mode to restrict
                                        access
                                      </FormDescription>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={platformForm.control}
                                name="autoArchiveInactiveDays"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      Auto-Archive Inactive Days
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="30"
                                        max="365"
                                        {...field}
                                        onChange={(e) =>
                                          field.onChange(
                                            parseInt(e.target.value)
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormDescription>
                                      Days of inactivity before auto-archiving
                                      campaigns
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={platformForm.control}
                                name="enableAnalytics"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Enable Analytics
                                      </FormLabel>
                                      <FormDescription>
                                        Collect and display platform analytics
                                      </FormDescription>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={platformForm.control}
                                name="enableNotifications"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Enable Notifications
                                      </FormLabel>
                                      <FormDescription>
                                        Send email and in-app notifications
                                      </FormDescription>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={platformForm.control}
                                name="enableEmailSharing"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Enable Email Sharing
                                      </FormLabel>
                                      <FormDescription>
                                        Allow users to share campaigns via email
                                      </FormDescription>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={platformForm.control}
                                name="defaultEmailShareLimit"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      Default Email Share Limit
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="1"
                                        max="1000"
                                        {...field}
                                        onChange={(e) =>
                                          field.onChange(
                                            parseInt(e.target.value)
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormDescription>
                                      Default number of emails users can send
                                      per month
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="bg-primary hover:bg-primary/90"
                          >
                            {isLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="w-4 h-4 mr-2" />
                                Save Platform Settings
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </TabsContent>

                  {/* Security Settings Tab */}
                  <TabsContent value="security" className="mt-0 space-y-6 p-6">
                    <Form {...securityForm}>
                      <form
                        onSubmit={securityForm.handleSubmit(onSecuritySubmit)}
                        className="space-y-8"
                      >
                        {/* Authentication Settings */}
                        <div className="space-y-6">
                          <div>
                            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                              <Key className="w-5 h-5 text-primary" />
                              Authentication Settings
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <FormField
                                control={securityForm.control}
                                name="sessionTimeout"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      Session Timeout (minutes)
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="15"
                                        max="1440"
                                        {...field}
                                        onChange={(e) =>
                                          field.onChange(
                                            parseInt(e.target.value)
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormDescription>
                                      How long before users are automatically
                                      logged out
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={securityForm.control}
                                name="maxLoginAttempts"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Max Login Attempts</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="3"
                                        max="10"
                                        {...field}
                                        onChange={(e) =>
                                          field.onChange(
                                            parseInt(e.target.value)
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormDescription>
                                      Maximum failed login attempts before
                                      lockout
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={securityForm.control}
                                name="lockoutDuration"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      Lockout Duration (minutes)
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="5"
                                        max="60"
                                        {...field}
                                        onChange={(e) =>
                                          field.onChange(
                                            parseInt(e.target.value)
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormDescription>
                                      How long accounts are locked after max
                                      attempts
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={securityForm.control}
                                name="requireTwoFactor"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Require Two-Factor Auth
                                      </FormLabel>
                                      <FormDescription>
                                        Force all users to enable 2FA
                                      </FormDescription>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>

                          <Separator />

                          {/* Data Management */}
                          <div>
                            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                              <Database className="w-5 h-5 text-primary" />
                              Data Management
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <FormField
                                control={securityForm.control}
                                name="enableAuditLog"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Enable Audit Log
                                      </FormLabel>
                                      <FormDescription>
                                        Log all admin actions for security
                                      </FormDescription>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={securityForm.control}
                                name="dataRetentionDays"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Data Retention (days)</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="30"
                                        max="2555"
                                        {...field}
                                        onChange={(e) =>
                                          field.onChange(
                                            parseInt(e.target.value)
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormDescription>
                                      How long to keep user data before deletion
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={securityForm.control}
                                name="enableBackup"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Enable Auto Backup
                                      </FormLabel>
                                      <FormDescription>
                                        Automatically backup platform data
                                      </FormDescription>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={securityForm.control}
                                name="backupFrequency"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Backup Frequency</FormLabel>
                                    <FormControl>
                                      <select
                                        className="w-full p-3 border border-slate-200 rounded-lg bg-white"
                                        {...field}
                                      >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                      </select>
                                    </FormControl>
                                    <FormDescription>
                                      How often to perform automated backups
                                    </FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="bg-primary hover:bg-primary/90"
                          >
                            {isLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="w-4 h-4 mr-2" />
                                Save Security Settings
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </TabsContent>
                </Tabs>
              </CardHeader>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
